import { getPasswordIssues } from '@caterinx/shared-types';
import { Button } from '@caterinx/ui/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@caterinx/ui/components/ui/card';
import { Input } from '@caterinx/ui/components/ui/input';
import { Label } from '@caterinx/ui/components/ui/label';
import { PasswordInput } from '@caterinx/ui/components/ui/password-input';
import { Wordmark } from '@caterinx/ui/components/ui/wordmark';
import { useToast } from '@caterinx/ui/hooks/use-toast';
import { isAxiosError } from 'axios';
import { useState, type FormEvent } from 'react';
import { Link, useNavigate } from 'react-router-dom';

import { api } from '../../lib/api.js';

/**
 * Passwort zurücksetzen — beide Schritte auf einer Seite.
 *
 * Der Wechsel von „Code anfordern" zu „neues Passwort setzen" passiert im Zustand, nicht per
 * Navigation: Der Nutzer hat den Code gerade im Mailprogramm und soll ihn nicht auf einer
 * neu geladenen Seite eintippen müssen, auf der seine E-Mail-Adresse schon wieder weg ist.
 */
export function ForgotPasswordPage() {
  const { toast } = useToast();
  const navigate = useNavigate();
  const [step, setStep] = useState<'request' | 'confirm'>('request');
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const passwordIssues = newPassword ? getPasswordIssues(newPassword) : [];

  const handleRequest = async (formEvent: FormEvent): Promise<void> => {
    formEvent.preventDefault();
    setIsSubmitting(true);
    try {
      await api.post('/auth/forgot-password', { email });
      // Die API antwortet bewusst immer positiv — sie darf nicht verraten, welche
      // Adressen registriert sind. Die Oberfläche formuliert entsprechend vorsichtig.
      toast({
        title: 'E-Mail unterwegs',
        description: 'Falls ein Konto zu dieser Adresse existiert, erhalten Sie einen Code.',
      });
      setStep('confirm');
    } catch (error) {
      const data = isAxiosError(error) ? (error.response?.data as { message?: string }) : undefined;
      toast({ variant: 'destructive', title: 'Fehlgeschlagen', description: data?.message });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleConfirm = async (formEvent: FormEvent): Promise<void> => {
    formEvent.preventDefault();
    setIsSubmitting(true);
    try {
      await api.post('/auth/reset-password', { email, code, newPassword });
      toast({ title: 'Passwort geändert', description: 'Sie können sich jetzt anmelden.' });
      navigate('/login', { replace: true });
    } catch (error) {
      const data = isAxiosError(error) ? (error.response?.data as { message?: string }) : undefined;
      toast({ variant: 'destructive', title: 'Fehlgeschlagen', description: data?.message });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="flex min-h-screen flex-col items-center justify-center gap-8 p-4">
      <Wordmark className="w-44" />
      <Card className="w-full max-w-md">
        <CardHeader>
          <CardTitle>Passwort zurücksetzen</CardTitle>
          <CardDescription>
            {step === 'request'
              ? 'Wir schicken Ihnen einen Code an Ihre E-Mail-Adresse.'
              : 'Code aus der E-Mail eingeben und neues Passwort vergeben.'}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {step === 'request' ? (
            <form onSubmit={handleRequest} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="email">E-Mail</Label>
                <Input
                  id="email"
                  type="email"
                  required
                  value={email}
                  onChange={(changeEvent) => setEmail(changeEvent.target.value)}
                />
              </div>
              <Button type="submit" className="w-full" disabled={isSubmitting}>
                {isSubmitting ? 'Wird gesendet...' : 'Code anfordern'}
              </Button>
            </form>
          ) : (
            <form onSubmit={handleConfirm} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="code">Code</Label>
                <Input
                  id="code"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  required
                  value={code}
                  onChange={(changeEvent) => setCode(changeEvent.target.value)}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="newPassword">Neues Passwort</Label>
                <PasswordInput
                  id="newPassword"
                  autoComplete="new-password"
                  required
                  value={newPassword}
                  onChange={(changeEvent) => setNewPassword(changeEvent.target.value)}
                />
                {newPassword && passwordIssues.length > 0 && (
                  <p className="text-destructive text-sm">Mindestens 8 Zeichen, kein triviales Passwort.</p>
                )}
              </div>
              <Button type="submit" className="w-full" disabled={isSubmitting || passwordIssues.length > 0 || !code}>
                {isSubmitting ? 'Wird gespeichert...' : 'Passwort setzen'}
              </Button>
            </form>
          )}

          <p className="text-muted-foreground mt-6 text-sm">
            <Link to="/login" className="underline">
              Zurück zur Anmeldung
            </Link>
          </p>
        </CardContent>
      </Card>
    </div>
  );
}
